// Phase 7 finance depth (docs/clairvoyance 06 §6.4–6.6, 14 §14.4–14.6, 04 §4.6–4.7).
//
// FOR THE OFFICE SCREENS. Everything below is written by Cloud Functions only; clients READ the
// collections (scope rules in `firebase/firestore.rules`) and CALL the callables or SEND the commands.
//
// Collections (all server-written)
//   journals/{id}.journalNo         gap-free per entity and calendar year, stamped when the ledger applies the
//                                   journal (null until then); `journalCounters/{entityId}__{YYYY}` is server-only
//   periods/{entityId}__{YYYY-MM}   exists once a period was closed: status `closed`, checklist snapshot, history.
//                                   No document means the period is open.
//   budgets/{entityId}__{YYYY}      one document per entity and year: `months[YYYY-MM] = {lines{account: minor}, expectedBoxes}`
//   bankImportMaps/{walletId}       the CSV column map remembered per bank account
//   bankLines/{walletId}__{date}__{hash}  imported statement lines and what became of them
//   bankMatches/{walletId}__{journalId}   marker: this journal is matched to a bank line
//   bankRules/{id}                  saved user rules: "description contains X -> category Y"
//   bankReconciliations/{walletId}__{YYYY-MM}  status per bank account per month (read by the period checklist)
//   ledgerReconciliations/{entityId}__{YYYY-MM-DD}  result of the nightly reconcileLedger job
//   partnerCollections/{partnerId}__{entityId}  what a partner collected (1003), remitted and offset
//
// Commands (04 §4.6):   closePeriod · reopenPeriod · recordMoneyIn · recordPartnerRemittance ·
//                       matchBankLine · postBankLine · ignoreBankLine · upsertBankRule
//                       and new optional fields: `vat` on recordMoneyOut, recordBill and receiveEmptyBoxes;
//                       `offsetCollectionsMinor` on payBill.
// Callables (04 §4.7):  financeReport · setBudget · importBankStatement
// Jobs (04 §4.9):       reconcileLedger (nightly)
import { z } from 'zod';
import { sectorCodeSchema } from './config';
import { defineCommand } from './define';
import { MONEY_OUT_CATEGORIES, moneyLinkSchema } from './money';
import {
  apAgeingReportSchema,
  arAgeingReportSchema,
  cashBookReportSchema,
  fxRevaluationReportSchema,
  generalLedgerReportSchema,
  yearChecklistReportSchema,
} from './ledgerReports';

const minor = z.number().int().nonnegative().max(1e12);
const signedMinor = z.number().int().min(-1e13).max(1e13);
const iso = z.string();
const id = z.string().min(1).max(60);

export const periodSchema = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'A month like 2026-10');
export const accountCodeSchema = z.string().regex(/^\d{4}$/, 'A four-digit account code');

// ---------------------------------------------------------------- VAT input

/**
 * The supplier's VAT on a purchase (06 §6.3 "VAT input"). The amount paid is VAT-INCLUSIVE.
 * Give the rate (`rateBp`, basis points: 1500 = 15%, backed out of the gross) or the VAT amount printed on
 * the supplier invoice (`amountMinor`), never both. The invoice number and the supplier's VAT number are
 * kept on the 1040 line for the VAT return.
 */
export const vatInputSchema = z
  .object({
    rateBp: z.number().int().min(1).max(5000).optional(),
    amountMinor: minor.optional(),
    invoiceNo: z.string().trim().min(1).max(60),
    supplierVatNo: z.string().trim().min(1).max(30),
  })
  .strict()
  .refine((v) => (v.rateBp === undefined) !== (v.amountMinor === undefined), {
    message: 'Give the VAT rate or the VAT amount, not both and not neither',
    path: ['rateBp'],
  });
export type VatInput = z.infer<typeof vatInputSchema>;

// ------------------------------------------------------------------ periods

export const PERIOD_STATUSES = ['open', 'soft_closed', 'closed'] as const;
export type PeriodStatus = (typeof PERIOD_STATUSES)[number];

export const CHECKLIST_ITEM_KEYS = [
  'period_ended',
  'day_closes',
  'bank_reconciled',
  'unapplied_journals',
  'suspense',
  'trial_balance',
  'ledger_reconciled',
  'fx_revalued',
  'data_complete',
] as const;

/** One line of the period checklist. `fail` blocks the close, `info` and `pass` do not. */
export const checklistItemSchema = z
  .object({
    key: z.enum(CHECKLIST_ITEM_KEYS),
    label: z.string(),
    status: z.enum(['pass', 'fail', 'info']),
    detail: z.string(),
    /** What is outstanding, for the screen to list (branch and date, bank account, journal id). */
    open: z.array(z.string()).max(50).default([]),
  })
  .strict();
export type ChecklistItem = z.infer<typeof checklistItemSchema>;

export const periodChecklistSchema = z
  .object({
    entityId: sectorCodeSchema,
    period: periodSchema,
    items: z.array(checklistItemSchema),
    /** True when no item fails. */
    ready: z.boolean(),
    generatedAt: iso,
  })
  .strict();
export type PeriodChecklist = z.infer<typeof periodChecklistSchema>;

/** `periods/{entityId}__{YYYY-MM}`. Absent = open. */
export const periodDocSchema = z.object({
  entityId: sectorCodeSchema,
  period: periodSchema,
  status: z.enum(PERIOD_STATUSES),
  closedAt: iso.optional(),
  closedBy: z.string().optional(),
  checklist: periodChecklistSchema.optional(),
  /** Trial balance totals at the moment of closing (debits equal credits). */
  trialBalance: z.object({ debitMinor: signedMinor, creditMinor: signedMinor }).optional(),
  reopenCount: z.number().int().nonnegative().default(0),
  history: z
    .array(
      z.object({
        action: z.enum(['closed', 'reopen_requested', 'reopened']),
        by: z.string(),
        at: iso,
        reason: z.string().optional(),
        approvalId: z.string().optional(),
        approvedBy: z.string().optional(),
      }),
    )
    .default([]),
  updatedAt: iso,
});
export type PeriodDoc = z.infer<typeof periodDocSchema>;

/** `journalCounters/{entityId}__{YYYY}`: server only. The last journal number of an entity's year. */
export const journalCounterDocSchema = z.object({
  entityId: sectorCodeSchema,
  year: z.number().int(),
  lastNo: z.number().int().nonnegative(),
  updatedAt: iso,
});

export const closePeriod = defineCommand({
  type: 'closePeriod',
  permission: 'finance.period.close',
  principals: [],
  offline: false,
  payload: z
    .object({
      entityId: sectorCodeSchema,
      period: periodSchema,
      note: z.string().max(300).default(''),
    })
    .strict(),
});

/** Reopening needs a reason, and a second person with `finance.approve` (never the maker) decides it. */
export const reopenPeriod = defineCommand({
  type: 'reopenPeriod',
  permission: 'finance.period.close',
  principals: [],
  offline: false,
  payload: z
    .object({
      entityId: sectorCodeSchema,
      period: periodSchema,
      reason: z.string().trim().min(10).max(300),
    })
    .strict(),
});

// -------------------------------------------------------------- money in / out

export const MONEY_IN_CATEGORIES = [
  'owner_capital',
  'loan',
  'other_income',
  'refund_received',
] as const;
export type MoneyInCategory = (typeof MONEY_IN_CATEGORIES)[number];

/**
 * Money that is not a shipment payment arriving in a wallet (14 §14.4). One journal: Dr the wallet's
 * account, Cr 3000 owner capital · 2200 loans · 4900 other income · the account of `refundOf` (a refund
 * received from a supplier reduces that cost). Above the approval threshold it waits for a second person.
 */
export const recordMoneyIn = defineCommand({
  type: 'recordMoneyIn',
  permission: 'finance.payment.record',
  principals: [],
  offline: true,
  payload: z
    .object({
      walletId: z.string().min(1).max(40),
      category: z.enum(MONEY_IN_CATEGORIES),
      amountMinor: minor.refine((n) => n > 0, 'Amount must be more than zero'),
      payer: z.string().trim().min(1).max(120),
      /** For `refund_received`: the cost category that is being refunded (default `other`). */
      refundOf: z.enum(MONEY_OUT_CATEGORIES).optional(),
      link: moneyLinkSchema.optional(),
      note: z.string().max(300).default(''),
      receiptPath: z.string().max(200).optional(),
    })
    .strict(),
});

/** A partner hands over cash it collected for us (1003). Dr the wallet, Cr 1003. */
export const recordPartnerRemittance = defineCommand({
  type: 'recordPartnerRemittance',
  permission: 'finance.cash.receive',
  principals: [],
  offline: false,
  payload: z
    .object({
      partnerId: id,
      amountMinor: minor.refine((n) => n > 0, 'Amount must be more than zero'),
      walletId: z.string().min(1).max(40),
      reference: z.string().trim().min(1).max(80),
      note: z.string().max(300).default(''),
    })
    .strict(),
});

/** `partnerCollections/{partnerId}__{entityId}`: outstanding = collected − remitted − offset. */
export const partnerCollectionDocSchema = z.object({
  partnerId: z.string(),
  entityId: sectorCodeSchema,
  currency: z.string(),
  collectedMinor: minor,
  remittedMinor: minor.default(0),
  offsetMinor: minor.default(0),
  updatedAt: iso,
});

// ------------------------------------------------------------------ budgets

/**
 * `budgets/{entityId}__{YYYY}` (03 §3.9): per account per month, and the month's expected boxes.
 * Running-cost accounts (6xxx) add up to the monthly running-cost budget that feeds the running-cost
 * reserve (14 §14.6): per box = budget ÷ expectedBoxes, set aside when a shipment is paid in full.
 */
export const budgetDocSchema = z.object({
  entityId: sectorCodeSchema,
  year: z.number().int().min(2020).max(2100),
  currency: z.string(),
  months: z.record(
    periodSchema,
    z
      .object({
        lines: z.record(accountCodeSchema, minor).default({}),
        expectedBoxes: z.number().int().nonnegative().optional(),
      })
      .strict(),
  ),
  updatedAt: iso,
  updatedBy: z.string(),
  docVersion: z.number().int().nonnegative(),
});
export type BudgetDoc = z.infer<typeof budgetDocSchema>;

/** Callable `setBudget` (`finance.planning`): one cell of the budget, or the month's expected boxes. */
export const setBudgetInputSchema = z
  .object({
    entityId: sectorCodeSchema,
    month: periodSchema,
    accountCode: accountCodeSchema.optional(),
    category: z.enum(MONEY_OUT_CATEGORIES).optional(),
    amountMinor: minor.optional(),
    expectedBoxes: z.number().int().min(0).max(1_000_000).optional(),
  })
  .strict()
  .refine((v) => !(v.accountCode !== undefined && v.category !== undefined), {
    message: 'Give an account or a category, not both',
    path: ['category'],
  })
  .refine(
    (v) =>
      (v.accountCode !== undefined || v.category !== undefined) === (v.amountMinor !== undefined),
    { message: 'An account or category goes with an amount', path: ['amountMinor'] },
  )
  .refine((v) => v.amountMinor !== undefined || v.expectedBoxes !== undefined, {
    message: 'Nothing to set',
    path: ['amountMinor'],
  });
export type SetBudgetInput = z.infer<typeof setBudgetInputSchema>;

// ---------------------------------------------------------------- bank lines

export const BANK_LINE_STATUSES = [
  'unmatched',
  'suggested',
  'matched',
  'posted',
  'ignored',
] as const;
export type BankLineStatus = (typeof BANK_LINE_STATUSES)[number];

export const BANK_MAPPING_FIELDS = [
  'date',
  'description',
  'reference',
  'debit',
  'credit',
  'amount',
  'balance',
] as const;
/** Column index (0-based) per field. Either `debit` and `credit`, or one signed `amount`. */
export const bankMappingSchema = z
  .object({
    date: z.number().int().min(0).max(60).optional(),
    description: z.number().int().min(0).max(60).optional(),
    reference: z.number().int().min(0).max(60).optional(),
    debit: z.number().int().min(0).max(60).optional(),
    credit: z.number().int().min(0).max(60).optional(),
    amount: z.number().int().min(0).max(60).optional(),
    balance: z.number().int().min(0).max(60).optional(),
    /** How the date column is written. */
    dateFormat: z.enum(['iso', 'dmy', 'mdy']).default('iso'),
  })
  .strict();
export type BankMapping = z.infer<typeof bankMappingSchema>;

/** `bankImportMaps/{walletId}` */
export const bankImportMapDocSchema = z.object({
  walletId: z.string(),
  entityId: sectorCodeSchema,
  mapping: bankMappingSchema,
  hasHeader: z.boolean(),
  updatedBy: z.string(),
  updatedAt: iso,
});

/** What the matcher suggests for a line. */
export const bankSuggestionSchema = z
  .object({
    kind: z.enum(['journal', 'rule']),
    /** kind `journal`: the journal that moved the same money on the same bank account. */
    journalId: z.string().optional(),
    sourceType: z.string().optional(),
    score: z.number().int().min(0).max(200),
    reasons: z.array(z.string()).max(8),
    /** kind `rule`: the saved rule and the category it would post. */
    ruleId: z.string().optional(),
    category: z.string().optional(),
  })
  .strict();
export type BankSuggestion = z.infer<typeof bankSuggestionSchema>;

/** `bankLines/{walletId}__{date}__{hash}` */
export const bankLineDocSchema = z.object({
  id: z.string(),
  walletId: z.string(),
  entityId: sectorCodeSchema,
  currency: z.string(),
  date: z.iso.date(),
  period: periodSchema,
  description: z.string(),
  reference: z.string(),
  direction: z.enum(['in', 'out']),
  amountMinor: minor,
  balanceMinor: signedMinor.optional(),
  status: z.enum(BANK_LINE_STATUSES),
  suggestion: bankSuggestionSchema.optional(),
  matchedJournalId: z.string().optional(),
  /** A split match (06 §6.6): every entry this line was matched to, and the money each one carries. */
  matchedJournalIds: z.array(z.string()).optional(),
  split: z
    .array(z.object({ lineId: z.string(), journalId: z.string(), amountMinor: minor }).strict())
    .optional(),
  postedJournalId: z.string().optional(),
  category: z.string().optional(),
  ignoredReason: z.string().optional(),
  importId: z.string(),
  importedBy: z.string(),
  importedAt: iso,
  decidedBy: z.string().optional(),
  decidedAt: iso.optional(),
});
export type BankLineDoc = z.infer<typeof bankLineDocSchema>;

/** `bankRules/{id}`: "description contains X -> category Y". Applied at import, never silently posted. */
export const bankRuleSchema = z
  .object({
    id: z.string().regex(/^[a-z0-9_-]{3,60}$/),
    entityId: sectorCodeSchema,
    /** Limit to one bank account, or every account of the entity when absent. */
    walletId: z.string().min(1).max(40).optional(),
    field: z.enum(['description', 'reference', 'any']).default('description'),
    contains: z.string().trim().min(2).max(80),
    direction: z.enum(['in', 'out', 'any']).default('any'),
    /** A money-out category for money leaving, a money-in category for money arriving. */
    category: z.string().min(1).max(40),
    active: z.boolean().default(true),
  })
  .strict();
export type BankRule = z.infer<typeof bankRuleSchema>;

/** `bankReconciliations/{walletId}__{YYYY-MM}`: the status the period checklist reads. */
export const bankReconciliationDocSchema = z.object({
  walletId: z.string(),
  entityId: sectorCodeSchema,
  period: periodSchema,
  currency: z.string(),
  lineCount: z.number().int().nonnegative(),
  openCount: z.number().int().nonnegative(),
  matchedCount: z.number().int().nonnegative(),
  postedCount: z.number().int().nonnegative(),
  ignoredCount: z.number().int().nonnegative(),
  statementClosingMinor: signedMinor.optional(),
  /** `reconciled` when at least one line was imported and none is left to sort. */
  status: z.enum(['open', 'reconciled']),
  updatedAt: iso,
});
export type BankReconciliationDoc = z.infer<typeof bankReconciliationDocSchema>;

/** Callable `importBankStatement` (`finance.bank.manage`): rows as read from the CSV by the screen. */
export const importBankStatementInputSchema = z
  .object({
    walletId: z.string().min(1).max(40),
    rows: z
      .array(z.array(z.string().max(500)).max(60))
      .min(1)
      .max(5000),
    hasHeader: z.boolean().default(true),
    /** Absent: the map remembered for this account, else a guess from the headers. */
    mapping: bankMappingSchema.optional(),
    saveMapping: z.boolean().default(true),
    /** The statement's closing balance, if the screen read one (recorded on the last month). */
    closingBalanceMinor: signedMinor.optional(),
    fileName: z.string().max(120).optional(),
  })
  .strict();
export type ImportBankStatementInput = z.input<typeof importBankStatementInputSchema>;

export const importBankStatementOutputSchema = z
  .object({
    importId: z.string(),
    imported: z.number().int().nonnegative(),
    duplicates: z.number().int().nonnegative(),
    skipped: z.array(z.object({ row: z.number().int(), why: z.string() }).strict()).max(200),
    suggested: z.number().int().nonnegative(),
    unmatched: z.number().int().nonnegative(),
    mapping: bankMappingSchema,
    hasHeader: z.boolean(),
    periods: z.array(periodSchema),
  })
  .strict();
export type ImportBankStatementOutput = z.infer<typeof importBankStatementOutputSchema>;

/** Callable `bankMatchCandidates` (`finance.bank.manage`): what one line could be matched to by hand. */
export const bankMatchCandidatesInputSchema = z
  .object({ lineId: id, windowDays: z.number().int().min(1).max(31).default(14) })
  .strict();
export type BankMatchCandidatesInput = z.input<typeof bankMatchCandidatesInputSchema>;

export const bankMatchCandidatesOutputSchema = z
  .object({
    /** Entries on the account inside the window, not tied to a line yet, closest in date first. */
    journals: z
      .array(
        z
          .object({
            journalId: z.string(),
            sourceType: z.string(),
            businessDate: z.iso.date(),
            memo: z.string(),
            /** The entry's effect on the account: positive put money in. */
            netMinor: signedMinor,
          })
          .strict(),
      )
      .max(200),
    /** Other open lines of the same account and direction, for several lines that make one entry. */
    lines: z
      .array(
        z
          .object({
            id: z.string(),
            date: z.iso.date(),
            description: z.string(),
            reference: z.string(),
            amountMinor: minor,
          })
          .strict(),
      )
      .max(100),
    /** The smallest set of entries that adds up to the line exactly, if there is one. */
    exactSet: z.array(z.string()).optional(),
    capped: z.boolean(),
  })
  .strict();
export type BankMatchCandidatesOutput = z.infer<typeof bankMatchCandidatesOutputSchema>;

/**
 * Callable `linkedMoney` (04 §4.7, 14 §14.3): the money lines tied to one shipment, container, trip,
 * handover, pickup, branch or partner, newest first, only those the caller's scope covers. Read by
 * whoever holds `finance.reports.view`, `finance.expense.record` or `finance.approve`.
 */
export const linkedMoneyInputSchema = z.object({ link: moneyLinkSchema }).strict();
export type LinkedMoneyInput = z.input<typeof linkedMoneyInputSchema>;

export const linkedMoneyOutputSchema = z
  .object({
    link: moneyLinkSchema,
    items: z
      .array(
        z
          .object({
            journalId: z.string(),
            sourceType: z.string(),
            businessDate: z.iso.date(),
            memo: z.string(),
            /** `out` is a cost or a bill, `in` is income or a refund, `move` shifts money between wallets. */
            direction: z.enum(['out', 'in', 'move']),
            amountMinor: minor,
            currency: z.string(),
          })
          .strict(),
      )
      .max(200),
    /** Per currency, `move` lines left out. */
    totals: z
      .array(z.object({ currency: z.string(), outMinor: minor, inMinor: minor }).strict())
      .max(10),
    capped: z.boolean(),
  })
  .strict();
export type LinkedMoneyOutput = z.infer<typeof linkedMoneyOutputSchema>;

/**
 * Tie bank lines to the journals that moved the same money: the suggestion, one entry picked by hand,
 * one line to several entries, or several lines to one entry. The two sides must add up exactly.
 */
export const matchBankLine = defineCommand({
  type: 'matchBankLine',
  permission: 'finance.bank.manage',
  principals: [],
  offline: false,
  payload: z
    .object({
      lineId: id,
      /** One entry for the line (the suggestion, or one picked by hand). */
      journalId: z.string().min(1).max(200).optional(),
      /** Several entries that add up to the line exactly (a deposit of several payments). */
      journalIds: z.array(z.string().min(1).max(200)).min(1).max(50).optional(),
      /** Other open lines of the same account that, with `lineId`, make up one entry. */
      alsoLineIds: z.array(id).min(1).max(20).optional(),
    })
    .strict()
    .refine((p) => (p.journalId === undefined) !== (p.journalIds === undefined), {
      message: 'Give one journal or a list of journals',
      path: ['journalIds'],
    }),
});

/**
 * An unmatched line becomes a journal (a bank fee, interest, a payment nobody recorded):
 * Dr the category's account / Cr the bank for money out, Dr the bank / Cr the account for money in.
 * `saveRule` also remembers "description contains X -> this category".
 */
export const postBankLine = defineCommand({
  type: 'postBankLine',
  permission: 'finance.bank.manage',
  principals: [],
  offline: false,
  payload: z
    .object({
      lineId: id,
      category: z.string().min(1).max(40),
      note: z.string().max(300).default(''),
      saveRule: z
        .object({
          contains: z.string().trim().min(2).max(80),
          field: z.enum(['description', 'reference', 'any']).default('description'),
        })
        .strict()
        .optional(),
    })
    .strict(),
});

export const ignoreBankLine = defineCommand({
  type: 'ignoreBankLine',
  permission: 'finance.bank.manage',
  principals: [],
  offline: false,
  payload: z.object({ lineId: id, reason: z.string().trim().min(3).max(200) }).strict(),
});

/** Create or change a saved rule (or switch it off with `active: false`). */
export const upsertBankRule = defineCommand({
  type: 'upsertBankRule',
  permission: 'finance.bank.manage',
  principals: [],
  offline: false,
  payload: z.object({ rule: bankRuleSchema }).strict(),
});

// --------------------------------------------------------------- ledger checks

/** `ledgerReconciliations/{entityId}__{YYYY-MM-DD}`: the nightly check that balances equal the journals. */
export const ledgerReconciliationDocSchema = z.object({
  entityId: sectorCodeSchema,
  date: z.iso.date(),
  ranAt: iso,
  journalsChecked: z.number().int().nonnegative(),
  unappliedJournals: z.number().int().nonnegative(),
  /** Journals read before the cap; true when the entity has more. */
  truncated: z.boolean(),
  walletMismatches: z
    .array(
      z.object({
        walletId: z.string(),
        expectedMinor: signedMinor,
        actualMinor: signedMinor,
      }),
    )
    .max(100),
  glMismatches: z
    .array(
      z.object({
        period: periodSchema,
        accountCode: accountCodeSchema,
        branchId: z.string(),
        expectedDebitMinor: signedMinor,
        expectedCreditMinor: signedMinor,
        actualDebitMinor: signedMinor,
        actualCreditMinor: signedMinor,
      }),
    )
    .max(100),
  /** Journal numbers that are missing or used twice in a year. */
  numberingProblems: z.array(z.string()).max(50),
  /** Journals posted into a month after it was closed (the lock was bypassed somewhere). */
  closedPeriodPostings: z.array(z.string()).max(50).default([]),
  ok: z.boolean(),
  exceptionId: z.string().optional(),
  alertId: z.string().optional(),
});
export type LedgerReconciliationDoc = z.infer<typeof ledgerReconciliationDocSchema>;

// -------------------------------------------------------------------- reports

export const FINANCE_REPORT_KINDS = [
  'trial_balance',
  'pnl',
  'balance_sheet',
  'vat_return',
  'budget_vs_actual',
  'consolidated',
  'period_checklist',
  'bank_reconciliation',
  'general_ledger',
  'cash_book',
  'ar_ageing',
  'ap_ageing',
  'fx_revaluation',
  'year_checklist',
] as const;
export type FinanceReportKind = (typeof FINANCE_REPORT_KINDS)[number];

/**
 * Callable `financeReport` (`finance.reports.view`, and the caller's scope must cover the entity). Returns
 * structured data, never a file. `entityId` is required for every kind but `consolidated`, which covers
 * every entity and refuses unless the caller's access is `all`.
 * `period` is a month; the trial balance and balance sheet are cumulative to the end of it, the P&L,
 * VAT return and budget vs actual are the movement within it.
 */
export const financeReportInputSchema = z
  .object({
    kind: z.enum(FINANCE_REPORT_KINDS),
    entityId: sectorCodeSchema.optional(),
    period: periodSchema,
    /** `consolidated` only. Default AED. */
    reportingCurrency: z.string().length(3).optional(),
    /** `general_ledger`: the account. */
    accountCode: accountCodeSchema.optional(),
    /** `cash_book`: the wallet. */
    walletId: z.string().min(1).max(60).optional(),
    /** `general_ledger`, `cash_book`: the dates, default the month; at most 366 days apart. */
    from: z.iso.date().optional(),
    to: z.iso.date().optional(),
    /** `ar_ageing`, `ap_ageing`: the day to age on, default the month's last day. */
    asOf: z.iso.date().optional(),
    /**
     * `consolidated` only: also count the system test's own countries (records marked `testData`).
     * Off by default, so test runs on staging never change the group figures.
     */
    includeTestData: z.boolean().optional(),
  })
  .strict()
  .refine((v) => v.kind === 'consolidated' || v.entityId !== undefined, {
    message: 'Choose an entity',
    path: ['entityId'],
  })
  .refine((v) => v.kind !== 'general_ledger' || v.accountCode !== undefined, {
    message: 'Choose an account',
    path: ['accountCode'],
  })
  .refine((v) => v.kind !== 'cash_book' || v.walletId !== undefined, {
    message: 'Choose a wallet',
    path: ['walletId'],
  })
  .refine((v) => !v.from || !v.to || v.from <= v.to, {
    message: 'The first date is after the last one',
    path: ['from'],
  })
  .refine(
    (v) =>
      !v.from ||
      !v.to ||
      Date.parse(`${v.to}T00:00:00Z`) - Date.parse(`${v.from}T00:00:00Z`) <= 366 * 86_400_000,
    { message: 'At most a year at a time', path: ['to'] },
  );
export type FinanceReportInput = z.input<typeof financeReportInputSchema>;

export const reportAccountRowSchema = z
  .object({
    accountCode: accountCodeSchema,
    name: z.string(),
    type: z.string(),
    debitMinor: signedMinor,
    creditMinor: signedMinor,
  })
  .strict();

export const trialBalanceReportSchema = z
  .object({
    kind: z.literal('trial_balance'),
    entityId: sectorCodeSchema,
    currency: z.string(),
    period: periodSchema,
    rows: z.array(reportAccountRowSchema),
    totalDebitMinor: signedMinor,
    totalCreditMinor: signedMinor,
    balanced: z.boolean(),
    truncated: z.boolean(),
    generatedAt: iso,
  })
  .strict();
export type TrialBalanceReport = z.infer<typeof trialBalanceReportSchema>;

export const statementLineSchema = z
  .object({ accountCode: accountCodeSchema, name: z.string(), amountMinor: signedMinor })
  .strict();

export const pnlReportSchema = z
  .object({
    kind: z.literal('pnl'),
    entityId: sectorCodeSchema,
    currency: z.string(),
    period: periodSchema,
    revenue: z.array(statementLineSchema),
    costOfSales: z.array(statementLineSchema),
    operatingCosts: z.array(statementLineSchema),
    other: z.array(statementLineSchema),
    revenueMinor: signedMinor,
    costOfSalesMinor: signedMinor,
    grossMarginMinor: signedMinor,
    operatingCostsMinor: signedMinor,
    otherMinor: signedMinor,
    netResultMinor: signedMinor,
    truncated: z.boolean(),
    generatedAt: iso,
  })
  .strict();
export type PnlReport = z.infer<typeof pnlReportSchema>;

export const balanceSheetReportSchema = z
  .object({
    kind: z.literal('balance_sheet'),
    entityId: sectorCodeSchema,
    currency: z.string(),
    period: periodSchema,
    assets: z.array(statementLineSchema),
    liabilities: z.array(statementLineSchema),
    equity: z.array(statementLineSchema),
    /** Profit or loss since the books began, until a year-end close moves it to 3200. */
    currentResultMinor: signedMinor,
    totalAssetsMinor: signedMinor,
    totalLiabilitiesMinor: signedMinor,
    totalEquityMinor: signedMinor,
    balanced: z.boolean(),
    truncated: z.boolean(),
    generatedAt: iso,
  })
  .strict();
export type BalanceSheetReport = z.infer<typeof balanceSheetReportSchema>;

export const vatReturnReportSchema = z
  .object({
    kind: z.literal('vat_return'),
    entityId: sectorCodeSchema,
    currency: z.string(),
    period: periodSchema,
    /** Net of discounts and credit notes: 4000 + 4010 + 4100 − 4800 movement of the month. */
    standardRatedSalesMinor: signedMinor,
    outputVatMinor: signedMinor,
    inputVatMinor: signedMinor,
    /** Output − input. Positive: pay the authority; negative: a refund or carry-forward. */
    netVatPayableMinor: signedMinor,
    inputLines: z
      .array(
        z
          .object({
            journalId: z.string(),
            journalNo: z.number().int().nullable(),
            date: z.iso.date(),
            invoiceNo: z.string(),
            supplierVatNo: z.string(),
            netMinor: signedMinor,
            vatMinor: signedMinor,
          })
          .strict(),
      )
      .max(5000),
    truncated: z.boolean(),
    generatedAt: iso,
  })
  .strict();
export type VatReturnReport = z.infer<typeof vatReturnReportSchema>;

export const budgetVsActualReportSchema = z
  .object({
    kind: z.literal('budget_vs_actual'),
    entityId: sectorCodeSchema,
    currency: z.string(),
    period: periodSchema,
    rows: z.array(
      z
        .object({
          accountCode: accountCodeSchema,
          name: z.string(),
          budgetMinor: signedMinor,
          actualMinor: signedMinor,
          /** Actual − budget: positive is overspent (for costs). */
          varianceMinor: signedMinor,
          usedPercent: z.number().nullable(),
        })
        .strict(),
    ),
    budgetTotalMinor: signedMinor,
    actualTotalMinor: signedMinor,
    expectedBoxes: z.number().int().nullable(),
    /** Running-cost budget ÷ expected boxes (14 §14.6); null without a budget or expected boxes. */
    runningReservePerBoxMinor: z.number().int().nullable(),
    generatedAt: iso,
  })
  .strict();
export type BudgetVsActualReport = z.infer<typeof budgetVsActualReportSchema>;

export const bankReconciliationReportSchema = z
  .object({
    kind: z.literal('bank_reconciliation'),
    entityId: sectorCodeSchema,
    period: periodSchema,
    accounts: z.array(
      z
        .object({
          walletId: z.string(),
          name: z.string(),
          currency: z.string(),
          status: z.enum(['open', 'reconciled', 'no_statement', 'no_activity']),
          lineCount: z.number().int(),
          openCount: z.number().int(),
          matchedCount: z.number().int(),
          postedCount: z.number().int(),
          ignoredCount: z.number().int(),
          /** Journals that moved this account in the month and are tied to no bank line yet. */
          unmatchedBookItems: z.number().int(),
          statementClosingMinor: signedMinor.nullable(),
        })
        .strict(),
    ),
    generatedAt: iso,
  })
  .strict();
export type BankReconciliationReport = z.infer<typeof bankReconciliationReportSchema>;

const translatedLineSchema = z
  .object({
    accountCode: accountCodeSchema,
    name: z.string(),
    /** Per entity, in the entity's currency. */
    byEntity: z.record(z.string(), signedMinor),
    /** The group figure in the reporting currency. */
    amountMinor: signedMinor,
  })
  .strict();

export const consolidatedReportSchema = z
  .object({
    kind: z.literal('consolidated'),
    reportingCurrency: z.string(),
    period: periodSchema,
    entities: z.array(
      z
        .object({
          entityId: sectorCodeSchema,
          currency: z.string(),
          /** Rates as decimal text; absent for an entity already in the reporting currency. */
          averageRate: z.string().nullable(),
          closingRate: z.string().nullable(),
          /** `average` of the rates dated inside the month, or `closing` when none was (a warning). */
          averageRateBasis: z.enum(['same_currency', 'average', 'closing']),
          netResultLocalMinor: signedMinor,
          netResultMinor: signedMinor,
        })
        .strict(),
    ),
    pnl: z
      .object({
        revenue: z.array(translatedLineSchema),
        costOfSales: z.array(translatedLineSchema),
        operatingCosts: z.array(translatedLineSchema),
        other: z.array(translatedLineSchema),
        revenueMinor: signedMinor,
        costOfSalesMinor: signedMinor,
        grossMarginMinor: signedMinor,
        operatingCostsMinor: signedMinor,
        otherMinor: signedMinor,
        netResultMinor: signedMinor,
      })
      .strict(),
    balanceSheet: z
      .object({
        assets: z.array(translatedLineSchema),
        liabilities: z.array(translatedLineSchema),
        equity: z.array(translatedLineSchema),
        /** Profit before this month, at the closing rate. */
        priorResultMinor: signedMinor,
        /** This month's result, at the average rate (the same figure as the P&L). */
        currentResultMinor: signedMinor,
        /** 3300: this month's result at the closing rate minus at the average rate. */
        translationDifferenceMinor: signedMinor,
        totalAssetsMinor: signedMinor,
        totalLiabilitiesMinor: signedMinor,
        totalEquityMinor: signedMinor,
        balanced: z.boolean(),
      })
      .strict(),
    eliminations: z.array(
      z
        .object({
          fromEntityId: sectorCodeSchema,
          toEntityId: sectorCodeSchema,
          /** What `toEntityId` shows as receivable from `fromEntityId` (1200), in the reporting currency. */
          receivableMinor: signedMinor,
          /** What `fromEntityId` shows as payable to `toEntityId` (2100), in the reporting currency. */
          payableMinor: signedMinor,
          eliminatedMinor: signedMinor,
          /** Receivable − payable that did not cancel (FX or timing): stays on the balance sheet. */
          differenceMinor: signedMinor,
        })
        .strict(),
    ),
    warnings: z.array(z.string()),
    truncated: z.boolean(),
    generatedAt: iso,
  })
  .strict();
export type ConsolidatedReport = z.infer<typeof consolidatedReportSchema>;

export const financeReportSchema = z.discriminatedUnion('kind', [
  trialBalanceReportSchema,
  pnlReportSchema,
  balanceSheetReportSchema,
  vatReturnReportSchema,
  budgetVsActualReportSchema,
  consolidatedReportSchema,
  bankReconciliationReportSchema,
  periodChecklistSchema.extend({ kind: z.literal('period_checklist') }),
  generalLedgerReportSchema,
  cashBookReportSchema,
  arAgeingReportSchema,
  apAgeingReportSchema,
  fxRevaluationReportSchema,
  yearChecklistReportSchema,
]);
export type FinanceReport = z.infer<typeof financeReportSchema>;

/** A missing FX rate blocks the consolidated report (06 §6.5); the callable's error details carry this list. */
export const missingRateSchema = z
  .object({
    from: z.string(),
    to: z.string(),
    date: z.iso.date(),
    purpose: z.enum(['average', 'closing']),
  })
  .strict();
export type MissingRate = z.infer<typeof missingRateSchema>;

export const FINANCE_COMMANDS = {
  closePeriod,
  reopenPeriod,
  recordMoneyIn,
  recordPartnerRemittance,
  matchBankLine,
  postBankLine,
  ignoreBankLine,
  upsertBankRule,
} as const;
